import { useAppDispatch, useAppSelector } from "@/store/hooks";
import axios from "axios";
import notification from "./notification.util";
import { setTokens, signOut } from "@/store/slicers/auth.slicer";

/**
 * A single in-flight refresh shared across every request that 401s at once, so a burst of parallel
 * calls triggers exactly one /auth/refresh — not one per request. Module-scoped (not per hook
 * instance) so all axios clients created by useApiClient coordinate through it.
 */
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = (refreshToken: string, dispatch: ReturnType<typeof useAppDispatch>) => {
  if (!refreshPromise) {
    // A bare axios call (not the app instance) so it carries no stale token and can't recurse
    // back through this interceptor.
    refreshPromise = axios
      .post(`${import.meta.env.VITE_API_URL}/auth/refresh`, { refresh_token: refreshToken })
      .then((res) => {
        const newToken: string | undefined = res.data?.token;
        if (!newToken) return null;
        const newRefresh: string | undefined = res.data?.refresh_token;
        dispatch(setTokens({ token: newToken, refreshToken: newRefresh ?? refreshToken }));
        return newToken;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const useApiClient = () => {
  const { token, refreshToken } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const instance = axios.create({
    baseURL: `${import.meta.env.VITE_API_URL}/`,
    headers,
    maxBodyLength: Infinity,
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const response = error?.response;
      const status = response?.status;
      const message = response?.data?.message;
      const original = error?.config;

      // An expired access token mid-session should refresh silently and replay the request, rather
      // than bounce the user to login. Skip the auth endpoints themselves, and only retry once.
      const isAuthPath =
        typeof original?.url === "string" &&
        (original.url.includes("/auth/login") || original.url.includes("/auth/refresh"));
      if (status === 401 && refreshToken && original && !original._retry && !isAuthPath) {
        original._retry = true;
        const newToken = await refreshAccessToken(refreshToken, dispatch);
        if (newToken) {
          original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
          return instance(original);
        }
        // Refresh token also expired/revoked — the session is unrecoverable.
        notification.error("Your session has expired. Please sign in again.");
        dispatch(signOut());
        return Promise.reject(error);
      }

      // Module-visibility 403s are intentional access restrictions, not errors the user should be
      // nagged about (the section is hidden from them anyway). Suppress those toasts.
      // 403s the UI already accounts for shouldn't nag the user. The API tags them with a `code`;
      // the message checks are fallbacks for a backend that predates it.
      const code = response?.data?.code;
      const isModuleDisabled =
        status === 403 &&
        (code === "MODULE_HIDDEN" || (typeof message === "string" && message.includes("disabled for your business")));

      // A plan gate on a READ is a background query for a section the plan doesn't include — the nav
      // already hides it, so the toast is pure noise. On a write the user deliberately did something,
      // so they get told why it was refused and what to upgrade.
      const isBackgroundPlanGate =
        status === 403 &&
        (original?.method ?? "get").toLowerCase() === "get" &&
        (code === "PLAN_GATE" ||
          (typeof message === "string" &&
            (message.includes("requires an active subscription") || message.includes("Upgrade to use it"))));

      if (message && !isModuleDisabled && !isBackgroundPlanGate) notification.error(message);
      if (status === 401) {
        dispatch(signOut());
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const makeParams = (params: any) => {
  Object.keys(params).forEach((k) => params[k] == null && delete params[k]);
  return params;
};
